import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Sparkles, ChevronLeft, Loader2, MapPin, Store, Gem, X, Sparkle, CalendarClock } from "lucide-react";
import { toast } from "sonner";
import ServicesShowcase from "../components/ServicesShowcase";

export default function Experiences() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [params] = useSearchParams();
  const [occasions, setOccasions] = useState([]);
  const [prompt, setPrompt] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [checkout, setCheckout] = useState(null);
  const [tab, setTab] = useState("ai");

  useEffect(() => { api.get("/experiences/occasions").then(({ data }) => setOccasions(data.occasions || [])); }, []);
  useEffect(() => { const q = params.get("q"); if (q) { setPrompt(q); generate(q, null); } /* eslint-disable-next-line */ }, []);

  const generate = async (text, occasion) => {
    const p = (text ?? prompt).trim();
    if (!p && !occasion) { toast.error("Descrivi cosa desideri o scegli un'occasione"); return; }
    setBusy(true); setResult(null);
    try {
      const { data } = await api.post("/experiences/generate", { prompt: p, occasion, zone: user?.zone });
      setResult(data);
      if ((data.proposals || []).length === 0) toast.message(data.message || "Nessuna esperienza disponibile al momento.");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Errore nella generazione"); }
    finally { setBusy(false); }
  };

  const startBuy = (proposal) => {
    if (!user) { navigate("/login", { state: { from: { pathname: "/esperienze" } } }); return; }
    setCheckout({ proposal, address: user?.address || "", useCredits: false, busy: false });
  };

  return (
    <div className="min-h-screen bg-[#0A0A0C] text-white grain" data-testid="experiences-page">
      <div className="max-w-2xl mx-auto px-4 py-6 relative z-10">
        <button onClick={() => navigate("/esplora")} className="flex items-center gap-2 text-zinc-400 hover:text-white mb-4 text-sm" data-testid="exp-back"><ChevronLeft className="h-4 w-4" /> Mappa</button>

        <div className="flex items-center gap-3 mb-2">
          <div className="h-12 w-12 rounded-2xl bg-gradient-to-br from-[#7DF9FF]/20 to-[#00E676]/15 border border-[#7DF9FF]/30 flex items-center justify-center"><Sparkles className="h-6 w-6 text-[#7DF9FF]" /></div>
          <div><h1 className="text-2xl font-display font-black tracking-tight">Esperienze AI</h1><p className="text-sm text-zinc-400">Racconta cosa vuoi fare. Ci pensa l'AI a costruirla.</p></div>
        </div>

        <div className="flex gap-2 mt-4 bg-[#141417] border border-white/10 rounded-full p-1" data-testid="exp-tabs">
          <button data-testid="exp-tab-ai" onClick={() => setTab("ai")} className={`flex-1 text-sm rounded-full py-2 font-semibold transition-colors ${tab === "ai" ? "bg-gradient-to-r from-[#7DF9FF] to-[#00E676] text-black" : "text-zinc-400"}`}>✨ Crea con AI</button>
          <button data-testid="exp-tab-browse" onClick={() => setTab("browse")} className={`flex-1 text-sm rounded-full py-2 font-semibold transition-colors ${tab === "browse" ? "bg-gradient-to-r from-[#7DF9FF] to-[#00E676] text-black" : "text-zinc-400"}`}>🛍️ Sfoglia i servizi</button>
        </div>

        {tab === "browse" && <div className="mt-5"><ServicesShowcase /></div>}

        {tab === "ai" && (<>
        <div className="bg-[#141417] border border-white/10 rounded-2xl p-4 mt-4">
          <Textarea data-testid="exp-prompt" value={prompt} onChange={(e) => setPrompt(e.target.value)} rows={2}
            placeholder="Es. Serata romantica per stupire la mia ragazza · Ho 40€ · Piove, cosa facciamo?"
            className="bg-[#0A0A0C] border-white/10 resize-none" />
          <div className="flex flex-wrap gap-2 mt-3">
            {occasions.map((o) => (
              <button key={o.key} data-testid={`exp-occasion-${o.key}`} onClick={() => generate(o.label, o.key)}
                className="text-xs rounded-full border border-white/15 bg-white/5 px-3 py-1.5 hover:border-[#7DF9FF]/50 transition-colors">
                {o.emoji} {o.label}
              </button>
            ))}
          </div>
          <Button data-testid="exp-generate" onClick={() => generate()} disabled={busy} className="mt-3 w-full bg-gradient-to-r from-[#7DF9FF] to-[#00E676] text-black font-semibold rounded-full">
            {busy ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Sto componendo la tua esperienza…</> : <><Sparkles className="h-4 w-4 mr-1" /> Crea la mia esperienza</>}
          </Button>
        </div>

        {result && (result.proposals || []).length === 0 && (
          <div className="text-center text-zinc-500 py-12 border border-dashed border-white/10 rounded-2xl mt-5" data-testid="exp-empty">
            {result.message || "Nessuna esperienza disponibile in questo momento."}
          </div>
        )}

        <div className="space-y-4 mt-5">
          {(result?.proposals || []).map((p) => (
            <div key={p.id} className="bg-[#141417] border border-white/10 rounded-2xl p-4" data-testid={`exp-proposal-${p.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="font-display font-bold text-lg">{p.title}</div>
                  {p.story && <p className="text-sm text-zinc-400 mt-1 leading-relaxed">{p.story}</p>}
                </div>
                {result.source === "ai" && <span className="shrink-0 text-[10px] rounded-full bg-[#7DF9FF]/12 text-[#7DF9FF] border border-[#7DF9FF]/30 px-2 py-1 flex items-center gap-1"><Sparkles className="h-3 w-3" /> AI</span>}
              </div>
              <div className="mt-3 space-y-2">
                {p.steps.map((s, i) => (
                  <div key={i} className="flex items-center justify-between rounded-xl bg-[#0A0A0C] border border-white/10 px-3 py-2" data-testid={`exp-step-${p.id}-${i}`}>
                    <div className="min-w-0">
                      <div className="text-sm font-medium truncate flex items-center gap-1.5">
                        {s.kind === "service" && <span className="text-[9px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-[#7DF9FF]/15 text-[#7DF9FF] border border-[#7DF9FF]/30 shrink-0">Servizio</span>}
                        {s.name}
                      </div>
                      <div className="text-xs text-zinc-500 flex items-center gap-1">{s.kind === "service" ? <Sparkle className="h-3 w-3" /> : <Store className="h-3 w-3" />} {s.business_name}</div>
                    </div>
                    <div className="text-sm font-semibold text-[#00E676] shrink-0">{s.pricing_mode === "quote" ? <span className="text-xs text-zinc-400">Preventivo</span> : `€${s.price.toFixed(2)}`}</div>
                  </div>
                ))}
              </div>
              <div className="flex items-center justify-between mt-3">
                <div className="text-sm text-zinc-400">{p.n_businesses} attività · <span className="text-white font-semibold">€{p.subtotal.toFixed(2)}</span> <span className="text-xs text-zinc-500">+ consegna</span></div>
                <Button size="sm" onClick={() => startBuy(p)} className="bg-[#00E676] text-black rounded-full" data-testid={`exp-buy-${p.id}`}>Acquista esperienza</Button>
              </div>
            </div>
          ))}
        </div>
        </>)}
      </div>

      {checkout && <CheckoutDialog result={result} checkout={checkout} setCheckout={setCheckout} user={user} navigate={navigate} />}
    </div>
  );
}

function CheckoutDialog({ result, checkout, setCheckout, user, navigate }) {
  const { proposal } = checkout;
  const set = (k, v) => setCheckout((c) => ({ ...c, [k]: v }));
  const slotServices = (proposal.steps || []).filter((s) => s.kind === "service" && s.flow_mode === "slot_booking");
  const contactServices = (proposal.steps || []).filter((s) => s.kind === "service" && s.flow_mode !== "slot_booking");
  const [slotSel, setSlotSel] = useState({}); // service_id -> {date, time, slots, loading}

  const loadSlots = async (sid, date) => {
    setSlotSel((p) => ({ ...p, [sid]: { ...(p[sid] || {}), date, loading: true, time: "" } }));
    try {
      const { data } = await api.get(`/experiences/services/${sid}/slots?date=${date}`);
      setSlotSel((p) => ({ ...p, [sid]: { ...(p[sid] || {}), date, slots: data.slots || [], loading: false } }));
    } catch { setSlotSel((p) => ({ ...p, [sid]: { ...(p[sid] || {}), slots: [], loading: false } })); }
  };

  const confirm = async () => {
    if (!checkout.address.trim()) { toast.error("Inserisci un indirizzo di consegna"); return; }
    for (const s of slotServices) {
      const sel = slotSel[s.service_id];
      if (!sel || !sel.date || !sel.time) { toast.error(`Scegli data e ora per "${s.name}"`); return; }
    }
    set("busy", true);
    const service_slots = {};
    slotServices.forEach((s) => { const sel = slotSel[s.service_id]; service_slots[s.service_id] = { date: sel.date, time: sel.time }; });
    try {
      const { data } = await api.post("/experiences/checkout", {
        session_id: result.session_id, proposal_id: proposal.id, address: checkout.address.trim(),
        credits_used: checkout.useCredits ? (user?.credits || 0) : 0, service_slots, origin_url: window.location.origin,
      });
      if (data.checkout_url) { window.location.href = data.checkout_url; return; }
      toast.success("Esperienza confermata! 🎉"); navigate(`/app/esperienze/${data.experience_id}`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Checkout non riuscito"); set("busy", false); }
  };
  const today = new Date().toISOString().slice(0, 10);
  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={() => setCheckout(null)}>
      <div className="bg-[#141417] border border-white/10 rounded-2xl max-w-md w-full p-5 max-h-[88vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="exp-checkout">
        <div className="flex items-center justify-between mb-3"><div className="font-display font-bold text-lg">Conferma esperienza</div><button onClick={() => setCheckout(null)}><X className="h-5 w-5 text-zinc-400" /></button></div>
        <div className="text-sm text-zinc-400 mb-3">{proposal.title} · {proposal.n_businesses} attività</div>
        <label className="text-xs text-zinc-400 flex items-center gap-1"><MapPin className="h-3 w-3" /> Indirizzo di consegna</label>
        <Input data-testid="exp-address" value={checkout.address} onChange={(e) => set("address", e.target.value)} placeholder="Via, numero, comune" className="bg-[#0A0A0C] border-white/10 mt-1" />

        {slotServices.map((s) => {
          const sel = slotSel[s.service_id] || {};
          return (
            <div key={s.service_id} className="mt-4 rounded-xl bg-[#0A0A0C] border border-white/10 p-3" data-testid={`exp-slot-${s.service_id}`}>
              <div className="text-sm font-semibold flex items-center gap-1.5"><CalendarClock className="h-4 w-4 text-[#7DF9FF]" /> {s.name}</div>
              <div className="text-xs text-zinc-500 mb-2">Scegli data e ora per questa prenotazione</div>
              <Input type="date" min={today} value={sel.date || ""} onChange={(e) => loadSlots(s.service_id, e.target.value)} className="bg-[#141417] border-white/10" data-testid={`exp-slot-date-${s.service_id}`} />
              {sel.loading && <div className="text-xs text-zinc-500 mt-2"><Loader2 className="h-3 w-3 animate-spin inline" /> Carico gli orari…</div>}
              {sel.date && !sel.loading && (
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {(sel.slots || []).filter((x) => x.available).length === 0 && <span className="text-xs text-zinc-500">Nessuno slot libero in questa data.</span>}
                  {(sel.slots || []).filter((x) => x.available).map((x) => (
                    <button key={x.time} data-testid={`exp-slot-time-${s.service_id}-${x.time}`} onClick={() => setSlotSel((p) => ({ ...p, [s.service_id]: { ...p[s.service_id], time: x.time } }))}
                      className={`text-xs rounded-md px-2.5 py-1 border ${sel.time === x.time ? "bg-[#00E676] text-black border-[#00E676]" : "bg-white/5 text-zinc-300 border-white/15"}`}>{x.time}</button>
                  ))}
                </div>
              )}
            </div>
          );
        })}
        {contactServices.length > 0 && (
          <div className="mt-3 text-xs text-zinc-400 rounded-lg bg-[#7DF9FF]/5 border border-[#7DF9FF]/20 px-3 py-2">
            {contactServices.map((s) => s.name).join(", ")}: il fornitore ti contatterà per confermare i dettagli.
          </div>
        )}

        {(user?.credits || 0) > 0 && (
          <label className="flex items-center gap-2 text-sm mt-3 cursor-pointer" data-testid="exp-use-credits">
            <input type="checkbox" checked={checkout.useCredits} onChange={(e) => set("useCredits", e.target.checked)} className="h-4 w-4 accent-[#00E676]" />
            <Gem className="h-4 w-4 text-[#C4B5FD]" /> Usa i miei {user.credits} crediti
          </label>
        )}
        <p className="text-xs text-zinc-500 mt-3">La consegna dei prodotti è calcolata per ogni attività al pagamento. I servizi rifiutati dal fornitore vengono rimborsati in crediti.</p>
        <Button data-testid="exp-confirm" onClick={confirm} disabled={checkout.busy} className="mt-4 w-full bg-[#00E676] text-black font-semibold rounded-full">
          {checkout.busy ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Attendi…</> : "Conferma e paga"}
        </Button>
      </div>
    </div>
  );
}
